// @ts-strict-ignore
/**
 * Reads the contents of a File into an ArrayBuffer.
 * @param file The File handle (Blob) to read.
 * @returns A Promise resolving with an ArrayBuffer containing the file contents.
 */
export async function readFileAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    let fileReader = new FileReader()

    fileReader.onload = () => resolve(fileReader.result as ArrayBuffer)
    fileReader.onerror = () => reject(fileReader.error)
    fileReader.onabort = () => reject(new Error('Aborted.'))

    fileReader.readAsArrayBuffer(file)
  })
}

/**
 * Shows the native file picker to the user to select a File.
 * @param accept An array of MIME types to filter files; might be ignored by the browser.
 * @param capture Sets the capture property of the HTMLInputElement used under the hood.
 * @returns A Promise resolving with a File if selected, or undefined if the user canceled the process.
 */
export async function openLocalFile(accept: string[] = [], capture?: string): Promise<File> {
  return new Promise<File>((resolve) => {
    let fileInput = document.createElement('input')
    let isDone = false

    // filter openable files (this is mostly a UI hint, as most browsers allow user-override)
    if (accept?.length > 0) {
      fileInput.accept = accept.join(',')
    }

    fileInput.type = 'file'
    fileInput.capture = capture
    fileInput.style.display = 'none'

    fileInput.addEventListener('change', (e) => {
      resolve(fileInput.files[0])
      isDone = true
    })

    // note: remember to always call the 'click' method synchronously, or browsers will block it
    // this is because the call must be done in response to a user-initiated click event (we are still synchronous with the 'openLocalFile' call here)
    fileInput.click()

    // not selecting anything will not trigger the "change" event
    // so what we do here is make assumptions about whether the file open dialog has been closed
    // this helps avoid getting the returned Promise stuck
    window.addEventListener('focus', function handleFocus() {
      window.removeEventListener('focus', handleFocus)

      setTimeout(() => {
        if (fileInput.files.length === 0 && !isDone) {
          resolve(undefined)
          isDone = true
        }
      }, 500)
    })

    document.addEventListener('mousemove', function handleMMove() {
      document.removeEventListener('mousemove', handleMMove)

      setTimeout(() => {
        if (fileInput.files.length === 0 && !isDone) {
          resolve(undefined)
          isDone = true
        }
      }, 500)
    })
  })
}

function objectToBlob(object: Object) {
  return new Blob([JSON.stringify(object)], { type: 'application/json' })
}

/**
 * Interactively download file via URL, Blob, or object
 *
 * @param file URL, JS Object, or Blob to download
 * @param filename the name of the downloaded file (optional)
 */
export function downloadFile(file: string | Blob | Object, filename: string = null) {
  const parsedFile = typeof file === 'object' && !(file instanceof Blob) ? objectToBlob(file) : file
  const objectURL = typeof parsedFile === 'string' ? parsedFile : window.URL.createObjectURL(parsedFile)
  const a = document.createElement('a')
  a.href = objectURL
  a.download = filename || ''
  a.rel = 'noreferrer noopener'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * Interactively download a string as a file
 *
 * @param string the string to be downloaded as a file
 * @param filename the name of the downloaded file (optional)
 */
export function downloadString(string: string, filename: string = null) {
  return downloadFile(new Blob([string], { type: 'text/plain' }), filename)
}
